import React, { useState, useEffect } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { doSuggest } from "../../helpers/search";
import { ISquizSuggestion } from "../../helpers/squiz";
import { useDebounce } from "../../helpers/useDebounce";
import { isLandingPath } from "../../helpers/routes";
import { Autocomplete } from "./Autocomplete";
import { Box, Heading, Hidden, Text } from "@origin-digital/ods-core";
import { FullPageCoverContainer, SearchContainer } from "./styles";

interface ISearchProps {
  heading?: string;
  subHeading?: string;
  searchPlaceholder?: string;
}

function useSuggestions(searchQuery: string): ISquizSuggestion[] {
  const [suggestions, setSuggestions] = React.useState<ISquizSuggestion[]>([]);

  React.useEffect(() => {
    let isActive = true;

    (async () => {
      const values = await doSuggest(searchQuery);
      if (isActive) {
        setSuggestions(values);
      }
    })();

    return () => {
      isActive = false;
    };
  }, [searchQuery]);

  return suggestions;
}

type SearchHeadingProps = {
  heading?: string;
  subHeading?: string;
};

const SearchHeading: React.FC<SearchHeadingProps> = ({
  heading,
  subHeading,
}) => (
  <>
    {heading && (
      <Box width="full">
        <Hidden above="sm">
          <Heading component="h1" variant="h2" align="center">
            {heading}
          </Heading>
        </Hidden>

        <Hidden below="md">
          <Heading component="h1" variant="h1" align="center">
            {heading}
          </Heading>
        </Hidden>
      </Box>
    )}
    {subHeading && (
      <Box width="full" marginTop="small">
        <Text align="center" variant="subtitle" wordBreak={true}>
          {subHeading}
        </Text>
      </Box>
    )}
  </>
);

export const Search: React.FC<ISearchProps> = ({
  heading,
  subHeading,
  searchPlaceholder,
}) => {
  const { pathname } = useLocation();
  const isLandingPage = isLandingPath(pathname);

  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get("term") || "");
  const [isFocused, setIsFocused] = useState(false);

  const searchQuery = useDebounce(query, 200);
  const suggestions = useSuggestions(searchQuery);
  const suggestionLabels = suggestions.flatMap((suggestion) =>
    typeof suggestion.disp === "string" ? [suggestion.disp] : []
  );

  useEffect(() => {
    const searchTerm = searchParams.get("term");
    setQuery((currentQuery) => {
      const normalizedSearchTerm = searchTerm || "";
      return normalizedSearchTerm !== currentQuery
        ? normalizedSearchTerm
        : currentQuery;
    });
  }, [searchParams]);

  return (
    <SearchContainer>
      {isLandingPage && (
        <SearchHeading heading={heading} subHeading={subHeading} />
      )}
      <Box
        width="full"
        marginTop={isLandingPage ? { xs: "xlarge", lg: "xxlarge" } : {}}
      >
        <Autocomplete
          onFocusChange={setIsFocused}
          inputValue={query}
          setInputValue={setQuery}
          suggestions={suggestionLabels}
          placeholder={searchPlaceholder}
        />
      </Box>
      <FullPageCoverContainer show={isFocused} />
    </SearchContainer>
  );
};
